import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Receipt, RotateCw, Search, X } from 'lucide-react';
import { toast } from '../../lib/toast';
import { api } from '../../api/client';
import EmptyState from '../../components/EmptyState';
import PaginationFooter from '../../components/PaginationFooter';
import { SkeletonTable } from '../../components/Skeleton';
import { formatDateTime } from '../../lib/format';
import BillingEventDrawer from '../../components/BillingEventDrawer';

const EVENT_LABEL = {
  'purchase.initial': { label: 'Sale', color: 'bg-emerald-50 text-emerald-700' },
  'purchase.rebill': { label: 'Rebill', color: 'bg-cyan-50 text-cyan-700' },
  'purchase.refund': { label: 'Refund', color: 'bg-amber-50 text-amber-700' },
  'purchase.chargeback': { label: 'Chargeback', color: 'bg-rose-50 text-rose-700' },
  'subscription.cancel': { label: 'Cancel', color: 'bg-gray-100 text-gray-700' },
  'subscription.expired': { label: 'Expired', color: 'bg-gray-100 text-gray-700' },
  unknown: { label: 'Unknown', color: 'bg-gray-100 text-gray-500' },
};

function StatusPill({ event }) {
  if (event.processingError) {
    return (
      <span className="pill bg-rose-50 text-rose-700" title={event.processingError}>
        Error
      </span>
    );
  }
  if (event.processedAt) {
    return <span className="pill bg-emerald-50 text-emerald-700">Processed</span>;
  }
  return <span className="pill bg-gray-100 text-gray-600">Pending</span>;
}

function EmailPill({ row }) {
  const status = row.purchaseEmail?.status;
  if (!status) return <span className="text-ink-300 text-xs">—</span>;
  if (status === 'sent' || status === 'queued') {
    return <span className="pill bg-emerald-50 text-emerald-700 text-xs">Sent</span>;
  }
  return (
    <span className="pill bg-rose-50 text-rose-700 text-xs" title={row.purchaseEmail.error || ''}>
      Failed
    </span>
  );
}

function formatCents(cents, currency = 'USD') {
  const n = (cents || 0) / 100;
  if (currency === 'USD') return `$${n.toFixed(2)}`;
  return `${n.toFixed(2)} ${currency}`;
}

export default function SalesPage() {
  const qc = useQueryClient();
  const [searchParams] = useSearchParams();
  const initialQ = searchParams.get('q') || '';
  const [searchInput, setSearchInput] = useState(initialQ);
  const [query, setQuery] = useState(initialQ);
  const [provider, setProvider] = useState('');
  const [eventType, setEventType] = useState('');
  const [processed, setProcessed] = useState('');
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState(null);
  const PAGE_SIZE = 25;

  useEffect(() => {
    const id = setTimeout(() => setQuery(searchInput), 300);
    return () => clearTimeout(id);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [query, provider, eventType, processed]);

  const params = new URLSearchParams();
  if (query) params.set('q', query);
  if (provider) params.set('provider', provider);
  if (eventType) params.set('eventType', eventType);
  if (processed) params.set('processed', processed);
  params.set('page', String(page));
  params.set('limit', String(PAGE_SIZE));

  const eventsQuery = useQuery({
    queryKey: ['admin-billing-events', query, provider, eventType, processed, page, PAGE_SIZE],
    queryFn: () => api.get(`/api/admin/billing-events?${params}`).then((r) => r.data),
    placeholderData: keepPreviousData,
  });

  const replayMutation = useMutation({
    mutationFn: (id) => api.post(`/api/admin/billing-events/${id}/replay`).then((r) => r.data),
    onSuccess: (data) => {
      toast.success(
        data.warning
          ? `Replayed with warning: ${data.warning}`
          : `Replayed. Entitlements applied: ${(data.appliedEntitlements || []).join(', ') || '—'}`,
      );
      qc.invalidateQueries({ queryKey: ['admin-billing-events'] });
    },
    onError: (err) => {
      toast.error(err?.response?.data?.error?.message || 'Replay failed');
    },
  });

  const events = eventsQuery.data?.events || [];
  const total = eventsQuery.data?.total ?? 0;
  const totalPages = eventsQuery.data?.totalPages ?? 1;
  const hasActiveFilter = Boolean(query || provider || eventType || processed);

  return (
    <>
      <div className="card">
        <div className="section-header flex-wrap gap-3">
          <div className="section-title">
            <span className="section-icon">
              <Receipt className="w-4 h-4" />
            </span>
            Sales / billing events
            {eventsQuery.data && (
              <span className="text-xs text-ink-400 font-normal ml-1">({total})</span>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400 pointer-events-none" />
              <input
                className="input pl-10 max-w-xs"
                placeholder="Search email or txn id…"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
            </div>
            <select className="input max-w-[140px]" value={provider} onChange={(e) => setProvider(e.target.value)}>
              <option value="">All providers</option>
              <option value="launchpad">Launchpad</option>
              <option value="manual">Manual</option>
            </select>
            <select className="input max-w-[160px]" value={eventType} onChange={(e) => setEventType(e.target.value)}>
              <option value="">All events</option>
              {Object.keys(EVENT_LABEL).map((k) => (
                <option key={k} value={k}>
                  {EVENT_LABEL[k].label}
                </option>
              ))}
            </select>
            <select className="input max-w-[140px]" value={processed} onChange={(e) => setProcessed(e.target.value)}>
              <option value="">All status</option>
              <option value="yes">Processed</option>
              <option value="no">Pending</option>
              <option value="error">Errored</option>
            </select>
            {hasActiveFilter && (
              <button
                type="button"
                className="btn-ghost text-xs"
                onClick={() => {
                  setSearchInput('');
                  setQuery('');
                  setProvider('');
                  setEventType('');
                  setProcessed('');
                }}
              >
                <X className="w-3 h-3" /> Clear
              </button>
            )}
          </div>
        </div>

        {eventsQuery.isLoading ? (
          <SkeletonTable rows={8} cols={6} />
        ) : !events.length ? (
          <EmptyState
            icon={Receipt}
            title={hasActiveFilter ? 'No events match.' : 'No billing events yet.'}
            body={
              hasActiveFilter
                ? 'Try clearing the filters.'
                : 'IPN events from Launchpad will appear here once your webhook URL is connected.'
            }
          />
        ) : (
          <div className="overflow-x-auto -mx-4 sm:mx-0">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-xs text-ink-500 uppercase tracking-wide">
                  <th className="text-left font-medium py-2 px-3">When</th>
                  <th className="text-left font-medium py-2 px-3">Event</th>
                  <th className="text-left font-medium py-2 px-3">Customer</th>
                  <th className="text-left font-medium py-2 px-3">SKU</th>
                  <th className="text-right font-medium py-2 px-3">Amount</th>
                  <th className="text-left font-medium py-2 px-3">Status</th>
                  <th className="text-left font-medium py-2 px-3">Email</th>
                  <th className="text-right font-medium py-2 px-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {events.map((ev) => {
                  const eventInfo = EVENT_LABEL[ev.eventType] || EVENT_LABEL.unknown;
                  return (
                    <tr
                      key={ev._id}
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => setSelectedId(ev._id)}
                    >
                      <td className="py-2 px-3 text-ink-700 whitespace-nowrap">
                        {formatDateTime(ev.createdAt)}
                      </td>
                      <td className="py-2 px-3">
                        <span className={`pill ${eventInfo.color}`}>{eventInfo.label}</span>
                      </td>
                      <td className="py-2 px-3">
                        <div className="text-ink-900">{ev.customerEmail}</div>
                        {ev.customerName && (
                          <div className="text-xs text-ink-400">{ev.customerName}</div>
                        )}
                      </td>
                      <td className="py-2 px-3 text-ink-700">{ev.internalSkuSlug || '—'}</td>
                      <td className="py-2 px-3 text-ink-700 text-right whitespace-nowrap">
                        {formatCents(ev.amountCents, ev.currency)}
                      </td>
                      <td className="py-2 px-3">
                        <StatusPill event={ev} />
                      </td>
                      <td className="py-2 px-3">
                        <EmailPill row={ev} />
                      </td>
                      <td className="py-2 px-3 text-right">
                        {ev.processingError && (
                          <button
                            type="button"
                            className="btn-ghost text-xs"
                            disabled={replayMutation.isPending}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!confirm('Replay this event? Entitlements will be re-applied.')) return;
                              replayMutation.mutate(ev._id);
                            }}
                            title="Re-run the processor against this event's raw payload"
                          >
                            <RotateCw className="w-3 h-3" /> Replay
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {eventsQuery.data && totalPages > 1 && (
          <PaginationFooter
            page={page}
            totalPages={totalPages}
            onPageChange={setPage}
            total={total}
            pageSize={PAGE_SIZE}
          />
        )}
      </div>

      <BillingEventDrawer id={selectedId} open={Boolean(selectedId)} onClose={() => setSelectedId(null)} />
    </>
  );
}
